"use client";

import React, { useCallback, useState } from "react";
import { Box } from "@mui/material";
import { FileInput, showToast } from "@int-traderjoes/ui-components-library";
import { FileDownloadIcon, FileDeleteIcon } from "@/assets/icons/Header/SvgUtils";
import { handleDownloadDocument } from "@/utils/helper";
import { useLanguageContext } from "@/context/LanguageContext";
import ConfirmationModal from "@/components/ConfirmationModal";
import {
  setConfirmationMessage,
  setConfirmationTittle,
  setHighlightedConfirmationMessage,
} from "@/components/DataTable/Datatable.variables";
import "@/components/Nutrition/Nutrition.scss";

export type DocumentsUploadItem = {
  id: string | number;
  fileName: string;
};

export type DocumentsUploadListProps = {
  documents: DocumentsUploadItem[];
  /** Required for single-file mode; can be a noop when `multiple` is true. */
  onFileChange: (file: File | null) => void;
  /**
   * When true, native multi-select; selected files are delivered via `onFilesChange`.
   * List/download/delete remain in this component — FileInput is selection-only.
   */
  multiple?: boolean;
  /** Called with all valid selected files when `multiple` is true. */
  onFilesChange?: (files: File[]) => void;
  onDelete: (documentId: string) => void;
  disabled?: boolean;
  isReadOnly?: boolean;
  isUploading?: boolean;
  fileInputKey?: number;
  fileInputValue?: File | null;
  setFileInputValue?: (file: File | null) => void;
  placeholder?: string;
  allowedTypes?: string[];
  toastMessage?: string;
  uploadButtonWidth?: number | string;
  /** Passed to `FileInput` when the field has validation errors. */
  fileInputError?: boolean;
  /** When true, only renders the document list (no upload control). */
  hideUpload?: boolean;
  /** When true, hides the delete button (download remains available). */
  hideDelete?: boolean;
  /** When true, keeps the delete button visible but disabled (faded). */
  deleteDisabled?: boolean;
  /**
   * When true, show confirmation modal before calling onDelete.
   * Default false keeps existing immediate-delete behavior (e.g. Compliance legacy, which confirms in the parent).
   */
  confirmBeforeDelete?: boolean;
};

/**
 * Shared document list with optional upload (`FileInput`).
 * Used by Other Labeling, discontinuation forms, and compliance legacy documents.
 */
const DocumentsUploadList: React.FC<DocumentsUploadListProps> = ({
  documents,
  onFileChange,
  multiple = false,
  onFilesChange,
  onDelete,
  disabled = false,
  isReadOnly = false,
  isUploading = false,
  fileInputKey = 0,
  fileInputValue = null,
  setFileInputValue,
  placeholder = "UPLOAD DOCUMENT",
  allowedTypes,
  toastMessage,
  uploadButtonWidth = 270,
  fileInputError = false,
  hideUpload = false,
  hideDelete = false,
  deleteDisabled = false,
  confirmBeforeDelete = false,
}) => {
  const { languageText } = useLanguageContext();
  const validationMessages = languageText?.MESSAGES?.VALIDATION_MESSAGES;
  const uploadDisabled = disabled || isReadOnly || isUploading;
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleRequestDelete = useCallback(
    (documentId: string) => {
      if (!confirmBeforeDelete) {
        onDelete(documentId);
        return;
      }
      setConfirmationTittle(
        languageText?.COMPONENT_SPECIFIC_CONTENT?.COMPLIANCE_DOCUMENTS?.DELETE_LEGACY_DOCUMENT_CONFIRM_TITLE
          || "Delete Document"
      );
      setConfirmationMessage(
        languageText?.MESSAGES?.CONFIRMATION_BOX_MESSAGES?.CONFIRM_MESSAGE?.DELETE_FILE_CONFIRM_MSG
          || "Are you sure you want to delete?"
      );
      setHighlightedConfirmationMessage("");
      setPendingDeleteId(documentId);
      setDeleteConfirmOpen(true);
    },
    [confirmBeforeDelete, onDelete, languageText]
  );

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setPendingDeleteId(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteId) {
      onDelete(pendingDeleteId);
      showToast({
        type: "success",
        message:
          languageText?.MESSAGES?.VALIDATION_MESSAGES?.FILE_VALIDATION?.FILE_DELETED_SUCCESS ||
          "File deleted successfully.",
      });
    }
    setDeleteConfirmOpen(false);
    setPendingDeleteId(null);
  }, [pendingDeleteId, onDelete, languageText]);

  const fileCount = documents.length;
  const listContainerSx =
    fileCount > 2
      ? {
          maxHeight: "200px",
          overflowY: "auto" as const,
          overflowX: "hidden" as const,
          padding: "8px",
          marginBottom: "16px",
        }
      : {
          overflowX: "hidden" as const,
          marginBottom: fileCount > 0 ? "16px" : undefined,
        };

  return (
    <Box>
      <Box className="nutrition-attachments-list" sx={listContainerSx}>
        {documents.map((document) => (
          <Box key={String(document.id)} className="nutrition-attachment-item">
            <Box className="nutrition-attachment-content">{document.fileName}</Box>
            <Box className="flex items-end justify-end gap-2">
              <button
                type="button"
                style={{
                  position: "absolute",
                  right: "50px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={() =>
                  handleDownloadDocument(
                    String(document.id),
                    document.fileName,
                    validationMessages
                  )
                }
                aria-label="Download document"
              >
                <FileDownloadIcon />
              </button>
              {!isReadOnly && !hideDelete && (
                <button
                  type="button"
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                  onClick={() => handleRequestDelete(String(document.id))}
                  disabled={deleteDisabled}
                  aria-label="Delete document"
                >
                  <FileDeleteIcon />
                </button>
              )}
            </Box>
          </Box>
        ))}
      </Box>
      {!hideUpload ? (
        <Box width={uploadButtonWidth}>
          <FileInput
            key={`documents-upload-${fileInputKey}`}
            multiple={multiple}
            onFileChange={(file) => {
              if (uploadDisabled || multiple) return;
              onFileChange(file);
            }}
            onFilesChange={
              multiple
                ? (files) => {
                    if (uploadDisabled) return;
                    onFilesChange?.(files);
                  }
                : undefined
            }
            placeholder={placeholder}
            value={multiple ? undefined : fileInputValue}
            setFileSelected={multiple ? undefined : setFileInputValue}
            disabled={uploadDisabled}
            allowedTypes={allowedTypes}
            toastMessage={toastMessage}
            error={fileInputError}
          />
        </Box>
      ) : null}

      {confirmBeforeDelete ? (
        <ConfirmationModal
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
          onDecline={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          confirmText={languageText?.BUTTONS?.DELETE ?? "Delete"}
          cancelText={languageText?.BUTTONS?.CANCEL_BUTTON ?? "Cancel"}
          showCancelButton
        />
      ) : null}
    </Box>
  );
};

export default DocumentsUploadList;
